/**
 * @file PlanChangesBar.tsx
 * @brief The one Save on Super Admin → Plans & seats: a bar stuck to the
 *        bottom, "3 changes · Review and save", that opens old → new first
 *
 * @details
 * DES 22-10-2026 (W8). Same save rule as W6: only changed keys are sent and
 * the list of what will change is shown before Save. Each line can be put
 * back on its own. The server's message (INVALID_PLAN_VALUE) and any
 * `ignoredKeys` show in the bar. Hidden when there is nothing to say.
 *
 * B20 / W17 (24-10-2026): under every change, "Applies: at once / from each
 * user's next billing period / new subscriptions only / next session" and
 * "Affects: N users, M companies" (../planChangeImpact.ts); prices add
 * "Existing subscribers keep their price". Save waits while a count is
 * coming; a backend without the impact endpoint shows "–" and doesn't block.
 * A Free reset-mode change keeps the Free numbers with it: they are listed
 * first under one caption (the numbers saved with the switch belong to the
 * new mode), a number that only changes meaning is shown, not sent, and
 * undoing the mode undoes them too.
 *
 * Related: SubscriptionPlansCard.tsx, ../launchPlans.ts changedPlanSettings,
 *   ../planChangeImpact.ts
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import type { PlanChange } from "../launchPlans";
import { PRICE_KEEP_NOTE, isPriceKey } from "../planChangeImpact";

/** What the bar says under one change (B20). */
export interface PlanChangeDetail {
  applies: string;
  affects: string;
  /** Key-specific counts and the server's note, in words. */
  extra: string[];
  /** The rule's own sentence (tooltip on "Applies"). */
  rule?: string | null;
}

/** A Free reset-mode change: these lines go together, first. */
export interface TogetherGroup {
  keys: string[];
  caption: string;
}

export function PlanChangesBar({
  lines: allLines,
  invalidCount,
  saving,
  saved,
  error,
  ignored,
  onSave,
  onUndo,
  onUndoAll,
  open,
  onOpenChange: setOpen,
  details,
  pending,
  together,
  ariaLabel = "Unsaved plan changes",
}: {
  lines: PlanChange[];
  invalidCount: number;
  saving: boolean;
  saved: boolean;
  error: string | null;
  ignored: string[];
  onSave: () => void;
  onUndo: (key: string) => void;
  onUndoAll: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Applies / Affects per changed key (B20). */
  details: Record<string, PlanChangeDetail>;
  /** A count is still coming: Save waits. */
  pending: boolean;
  together?: TogetherGroup | null;
  /** Links & company info reuses the bar (W19). */
  ariaLabel?: string;
}) {
  // Lines Save sends; implied ones (meaning only) are shown, not counted.
  const sent = allLines.filter((l) => !l.implied);
  const count = sent.length;
  const groupKeys = new Set(together?.keys ?? []);
  const grouped = allLines.filter((l) => groupKeys.has(l.key));
  const rest = allLines.filter((l) => !groupKeys.has(l.key));
  const lines = sent;
  // Nothing left to review: fold the list away.
  useEffect(() => {
    if (count === 0) setOpen(false);
  }, [count, setOpen]);

  const renderLine = (l: PlanChange) => {
    const d = details[l.key];
    return (
      <li key={l.key} className="py-1">
        <div className="flex items-baseline gap-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="min-w-0 flex-1 truncate text-muted-foreground">{l.label}</span>
            </TooltipTrigger>
            <TooltipContent side="top" className="font-mono text-[11px]">
              {l.key}
            </TooltipContent>
          </Tooltip>
          {/* Long values (links, W19) wrap instead of hiding the label. */}
          <span className="min-w-0 max-w-[65%] break-words text-right tabular-nums [overflow-wrap:anywhere]">
            <span className="text-muted-foreground line-through decoration-muted-foreground/50">
              {l.from || "not set"}
            </span>
            {" → "}
            <span className="font-semibold text-foreground">{l.to}</span>
          </span>
          {l.implied ? (
            <span className="w-[34px] shrink-0" aria-hidden />
          ) : (
            <button
              type="button"
              onClick={() => onUndo(l.key)}
              className="shrink-0 text-xs text-muted-foreground hover:text-foreground"
            >
              Undo
            </button>
          )}
        </div>
        {l.implied ? (
          <p className="mt-0.5 text-xs text-muted-foreground">Same number, new meaning. Not sent on its own.</p>
        ) : d ? (
          <div className="mt-0.5 space-y-0.5 text-xs text-muted-foreground">
            <p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    Applies: <span className="font-medium text-foreground/85">{d.applies}</span>
                  </span>
                </TooltipTrigger>
                {d.rule ? (
                  <TooltipContent side="top" className="max-w-[340px]">
                    {d.rule}
                  </TooltipContent>
                ) : null}
              </Tooltip>
              {" · "}
              Affects: <span className="font-medium text-foreground/85">{d.affects}</span>
            </p>
            {isPriceKey(l.key) ? <p className="font-medium text-foreground/85">{PRICE_KEEP_NOTE}</p> : null}
            {d.extra.map((x) => (
              <p key={x}>{x}</p>
            ))}
          </div>
        ) : null}
      </li>
    );
  };

  const message = error
    ? `Not saved. ${error}`
    : ignored.length
      ? `The server did not save: ${ignored.join(", ")}.`
      : null;
  if (count === 0 && invalidCount === 0 && !message && !saved) return null;

  const summary =
    count === 0
      ? invalidCount
        ? ""
        : "Saved."
      : `${count} change${count === 1 ? "" : "s"}`;

  return (
    <div className="pointer-events-none sticky bottom-4 z-20 flex justify-center px-4 pt-4">
      <div
        role="region"
        aria-label={ariaLabel}
        className="pointer-events-auto w-full max-w-2xl rounded-2xl bg-background shadow-[0_8px_30px_rgba(0,0,0,0.12)] ring-1 ring-black/[0.06] dark:shadow-[0_8px_30px_rgba(0,0,0,0.5)] dark:ring-white/[0.08]"
      >
        {open && count ? (
          <div className="max-h-[50vh] overflow-y-auto px-5 pt-4 text-sm">
            <p className="pb-1 text-xs font-semibold uppercase tracking-[0.08em] text-foreground/85">
              Save will change
            </p>
            {grouped.length ? (
              <div className="mb-2 rounded-xl bg-black/[0.03] px-3 py-2 ring-1 ring-black/[0.05] dark:bg-white/[0.04] dark:ring-white/[0.08]">
                <p className="text-xs font-medium text-foreground">{together?.caption}</p>
                <ul className="mt-1 divide-y divide-black/[0.04] dark:divide-white/[0.06]">{grouped.map(renderLine)}</ul>
              </div>
            ) : null}
            <ul className="divide-y divide-black/[0.04] dark:divide-white/[0.06]">{rest.map(renderLine)}</ul>
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
          <div className="min-w-0 flex-1 text-sm">
            {count ? (
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
                className="inline-flex max-w-full items-center gap-1.5 text-left"
              >
                <span className="shrink-0 font-semibold">{summary}</span>
                {open ? (
                  <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                ) : (
                  <>
                    <ChevronUp className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                    {/* One change, old → new, before the list is opened. */}
                    <span className="min-w-0 truncate text-muted-foreground">
                      {lines[count - 1].label}
                    </span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      {lines[count - 1].from || "not set"} →{" "}
                      <span className="font-medium text-foreground">{lines[count - 1].to}</span>
                      {count > 1 ? ` +${count - 1} more` : ""}
                    </span>
                  </>
                )}
              </button>
            ) : summary ? (
              <span className="font-semibold">{summary}</span>
            ) : null}
            {invalidCount ? (
              <span className="text-destructive">
                {summary ? " · " : ""}Fix {invalidCount} field{invalidCount === 1 ? "" : "s"} first
              </span>
            ) : null}
            {message ? <p className="mt-0.5 text-destructive">{message}</p> : null}
          </div>
          {count ? (
            <>
              <button
                type="button"
                onClick={onUndoAll}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Undo all
              </button>
              {open ? (
                <Button
                  onClick={onSave}
                  disabled={saving || pending || invalidCount > 0}
                  className="min-w-[148px] rounded-full"
                >
                  {saving
                    ? "Saving…"
                    : pending
                      ? "Counting who it affects…"
                      : `Save ${count} change${count === 1 ? "" : "s"}`}
                </Button>
              ) : (
                <Button onClick={() => setOpen(true)} className="min-w-[148px] rounded-full">
                  Review and save
                </Button>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
